/**
 * Global registry of every currently-open Modal, newest last.
 *
 * Modals across the app are rendered as independent siblings (PageWrapper alone
 * mounts nine of them) and each decides its own visibility, so two could easily
 * be on screen at the same time. This registry enforces one at a time: whichever
 * modal opened most recently is the visible one, and when it closes the previous
 * one becomes visible again.
 *
 * Suppressed modals are hidden with CSS rather than unmounted. Some modals are
 * rendered inside another modal's children (the Product Library filters modal
 * contains the save/load preset modals); unmounting the parent would unmount the
 * child, which would pop it off this stack, make the parent top-most again, and
 * remount the child — an endless loop.
 */

type Token = symbol;

let stack: Token[] = [];
const listeners = new Set<() => void>();

const emit = () => {
  listeners.forEach((listener) => listener());
};

export const subscribeToModalStack = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** The modal allowed to be visible, or undefined when none are open. */
export const getTopModal = (): Token | undefined => stack[stack.length - 1];

export const pushModal = (token: Token) => {
  if (stack.includes(token)) return;
  stack.push(token);
  emit();
};

export const removeModal = (token: Token) => {
  const index = stack.indexOf(token);
  if (index === -1) return;
  stack.splice(index, 1);
  emit();
};

/** Test helper — the stack is module state and would otherwise leak between tests. */
export const resetModalStack = () => {
  stack = [];
  emit();
};
